import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import {
  DiagramToCodePlugin,
  exportToBlob,
  getTextFromElements,
  MIME_TYPES,
  TTDDialog,
} from "../../packages/excalidraw";
import { getDataURL } from "../../packages/excalidraw/data/blob";
import { safelyParseJSON } from "../../packages/excalidraw/utils";

// Retrieve AI API URL and Key from Environment Variables
const AI_BACKEND_URL = import.meta.env.VITE_APP_AI_BACKEND;
const AI_API_KEY = import.meta.env.VITE_APP_AI_KEY;

export const AIComponents = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI;
}) => {
  return (
    <>
      {/* 🔹 Diagram to Code Plugin - Converts a diagram into code using OpenAI */}
      <DiagramToCodePlugin
        generate={async ({ frame, children }) => {
          const appState = excalidrawAPI.getAppState();

          // Export diagram as an image (JPEG)
          const blob = await exportToBlob({
            elements: children,
            appState: {
              ...appState,
              exportBackground: true,
              viewBackgroundColor: appState.viewBackgroundColor,
            },
            exportingFrame: frame,
            files: excalidrawAPI.getFiles(),
            mimeType: MIME_TYPES.jpg,
          });

          const dataURL = await getDataURL(blob);

          // Extract text from elements
          const textFromFrameChildren = getTextFromElements(children);
          const formattedText = Array.isArray(textFromFrameChildren)
            ? textFromFrameChildren.join(", ")
            : textFromFrameChildren;



          // Send request to OpenAI API
          const response = await fetch(`${AI_BACKEND_URL}/chat/completions`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${AI_API_KEY}`, // API Key added here
            },
            body: JSON.stringify({
              model: "gpt-4", // or "gpt-3.5-turbo"
              messages: [
                { role: "system", content: "You are an AI that converts diagrams into code." },
                { role: "user", content: `Convert this diagram into code: ${formattedText}` }


              ],
              temperature: 0.7,
            }),
          });

          // Handle errors
          if (!response.ok) {
            const errorText = await response.text();
            const errorJSON = safelyParseJSON(errorText);
            throw new Error(errorJSON?.message || errorText);
          }

          try {
            const { choices } = await response.json();
            if (!choices || choices.length === 0) {
              throw new Error("Generation failed (invalid response)");
            }
            return { html: `<pre>${choices[0].message.content}</pre>` };
          } catch (error) {
            throw new Error("Generation failed (invalid response)");
          }
        }}
      />

      {/* 🔹 Text to Diagram Plugin - Converts text into a diagram using OpenAI */}
      <TTDDialog
  onTextSubmit={async (input) => {
    try {
      const response = await fetch(`${AI_BACKEND_URL}/chat/completions`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${AI_API_KEY}`, // API Key added here
        },
        body: JSON.stringify({
          model: "gpt-4", // or "gpt-3.5-turbo"
          messages: [
            { role: "system", content: "You are an AI that generates Excalidraw-compatible diagrams from text descriptions." },
            {
              role: "user",
              content: `Generate a valid Mermaid.js diagram from the following description.
              Respond ONLY with the Mermaid syntax inside a Markdown code block. Do not include any additional text.

              Description: "${input}"`
            }

          ],
          temperature: 0.7,
        }),
      });

      const responseData = await response.json();
      console.log("OpenAI Response:", responseData); // Debugging log

      if (!responseData.choices || responseData.choices.length === 0) {
        throw new Error("Invalid response: No choices returned from OpenAI.");
      }

      // Extract Mermaid syntax from response
      const rawMermaidOutput = responseData.choices[0].message.content;

      // Ensure it's a string and extract Mermaid code block
      const mermaidMatch = rawMermaidOutput.match(/```mermaid\n([\s\S]+?)\n```/);
      if (!mermaidMatch) {
        throw new Error("OpenAI did not return a valid Mermaid diagram.");
      }

      const mermaidDiagram = mermaidMatch[1]; // Extracted Mermaid code

      return { generatedResponse: mermaidDiagram };

    } catch (err) {
      throw new Error("Request failed");
    }
  }}
/>

    </>
  );
};
